import React, { useEffect, useState } from 'react'
import { InventoryReturnSetting, InventoryItem } from '../types'
import { apiGet } from '../utils/api'

const InventoryReturnSettings: React.FC = () => {
  const [settings, setSettings] = useState<InventoryReturnSetting[]>([])
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingSetting, setEditingSetting] = useState<InventoryReturnSetting | null>(null)
  
  const [formData, setFormData] = useState({
    inventoryItemId: '',
    returnItemId: '',
    proportion: 0.7,
    defaultCostPrice: 0
  })

  // Загрузка данных
  const fetchData = async () => {
    setLoading(true)
    try {
      const [settingsData, itemsData] = await Promise.all([
        apiGet<InventoryReturnSetting[]>('/inventory-return-settings'),
        apiGet<any>('/inventory-items')
      ])
      
      setSettings(settingsData || [])
      setInventoryItems(itemsData.items || [])
    } catch (error) {
      console.error('Ошибка загрузки данных:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Обработчики формы
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.inventoryItemId || !formData.returnItemId) {
      alert('Выберите исходный и возвращаемый ТМЦ')
      return
    }
    
    if (formData.proportion <= 0 || formData.proportion > 1) {
      alert('Пропорция должна быть от 0 до 1')
      return
    }
    
    if (formData.defaultCostPrice <= 0) {
      alert('Цена должна быть больше 0')
      return
    }
    
    try {
      const url = editingSetting 
        ? `/api/inventory-return-settings/${editingSetting.id}`
        : '/api/inventory-return-settings'
      
      const method = editingSetting ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Ошибка сохранения')
      }
      
      await fetchData()
      handleFormClose()
    } catch (error) {
      console.error('Ошибка сохранения:', error)
      alert(error.message || 'Ошибка сохранения настройки')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingSetting(null)
    setFormData({
      inventoryItemId: '',
      returnItemId: '',
      proportion: 0.7,
      defaultCostPrice: 0
    })
  }

  const handleEdit = (setting: InventoryReturnSetting) => {
    setEditingSetting(setting)
    setFormData({
      inventoryItemId: setting.inventoryItemId,
      returnItemId: setting.returnItemId,
      proportion: setting.proportion,
      defaultCostPrice: setting.defaultCostPrice
    })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить настройку? Это действие нельзя отменить.')) return
    
    try {
      const response = await fetch(`/api/inventory-return-settings/${id}`, {
        method: 'DELETE'
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Ошибка удаления')
      }
      
      await fetchData()
    } catch (error) {
      console.error('Ошибка удаления:', error)
      alert(error.message || 'Ошибка удаления настройки')
    }
  }

  const getInventoryItem = (id: string) => {
    return inventoryItems.find(item => item.id === id)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Настройки возврата ТМЦ</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Создать настройку
        </button>
      </div>

      {/* Список настроек */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Исходный ТМЦ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Возвращаемый ТМЦ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Пропорция
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Цена по умолчанию
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  Загрузка...
                </td>
              </tr>
            ) : settings.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  Нет настроек
                </td>
              </tr>
            ) : (
              settings.map((setting) => (
                <tr key={setting.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {setting.inventoryItem?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {setting.returnItem?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(setting.proportion * 100).toFixed(0)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {setting.defaultCostPrice.toFixed(2)} MDL
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(setting)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Редактировать
                    </button>
                    <button
                      onClick={() => handleDelete(setting.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Модальное окно формы */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSetting ? 'Редактировать настройку' : 'Создать настройку возврата ТМЦ'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Исходный ТМЦ *
                    </label>
                    <select
                      value={formData.inventoryItemId}
                      onChange={(e) => setFormData({ ...formData, inventoryItemId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Выберите исходный ТМЦ</option>
                      {inventoryItems.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name} ({item.unit})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Возвращаемый ТМЦ *
                    </label>
                    <select
                      value={formData.returnItemId}
                      onChange={(e) => setFormData({ ...formData, returnItemId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Выберите возвращаемый ТМЦ</option>
                      {inventoryItems.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name} ({item.unit})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Пропорция (0.0 - 1.0) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={formData.proportion}
                      onChange={(e) => setFormData({ ...formData, proportion: parseFloat(e.target.value) || 0 })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Например: 0.7 = 70% (из 10 мячей можно вернуть 7)
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Цена по умолчанию (MDL) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.defaultCostPrice}
                      onChange={(e) => setFormData({ ...formData, defaultCostPrice: parseFloat(e.target.value) || 0 })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                </div>

                {/* Кнопки */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleFormClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingSetting ? 'Обновить' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InventoryReturnSettings

